/**
 * The data behind the editor's local listing preview.
 *
 * Pure derivation from what the editor already holds — the form state, the
 * cities the page loaded for the dropdown, and the images the editor knows
 * about. No request, no React, no window, no mutation of its inputs.
 *
 * This is deliberately NOT a client-side copy of the backend's
 * `_listing_to_api_shape`. The preview shows the handful of fields the public
 * card actually renders; the marketing v2 fields, the publication flags and
 * the sort order are admin metadata and are summarised elsewhere.
 *
 * Everything that is missing comes back as `null` rather than as a zero or an
 * empty string, so the card can say "not filled in yet" instead of inventing
 * "0 Schlafzimmer" for a field the user simply has not reached.
 */
import { cityLabel, formatChf } from "./listingFormatting";

/**
 * The description field stores MARKDOWN, not HTML: `RichTextField` is a
 * TipTap editor configured with `Markdown.configure({ html: false })` and
 * writes back `editor.storage.markdown.getMarkdown()`.
 *
 * So the preview strips markdown syntax rather than tags, and it does so with
 * string operations only — no `dangerouslySetInnerHTML`, no DOM parsing, no
 * sanitiser dependency. Anything this misses shows up as literal punctuation,
 * which is the safe direction to fail in.
 */
export function markdownToPlainText(value) {
  if (typeof value !== "string" || value.trim() === "") return "";
  return value
    // Fenced code: drop the fences, keep the code as text.
    .replace(/```[^\n]*\n?/g, "")
    .replace(/`([^`]*)`/g, "$1")
    // Images before links — an image is a link with a leading "!".
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    // Block syntax at the start of a line.
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s{0,3}([-*+]|\d+\.)\s+/gm, "")
    .replace(/^\s{0,3}(?:-\s*-\s*-|\*\s*\*\s*\*|_\s*_\s*_)[-*_\s]*$/gm, "")
    // Emphasis. Longest marker first so "**x**" does not leave a stray "*".
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    // The serializer escapes literal punctuation ("2\\*3"); unescape last so the
    // character the user typed survives instead of a stray backslash.
    .replace(/\\([\\`*_{}[\]()#+\-.!~>])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

/** A trimmed string, or null when there is nothing to show. */
function text(value) {
  const s = typeof value === "string" ? value.trim() : "";
  return s === "" ? null : s;
}

/**
 * A form number as the card should print it.
 *
 * The form keeps every numeric field as a string, and an untouched field is
 * "". Only a value that actually parses becomes a number — "0" counts, ""
 * does not, so a deliberate zero survives and an empty field disappears.
 */
function count(value) {
  const s = typeof value === "string" ? value.trim() : value;
  if (s === "" || s === null || s === undefined) return null;
  // parseInt, not Number: `buildListingPayload` sends `parseInt(field, 10)`, so
  // "1.5" is saved as 1 and the preview must not promise 1.5.
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * The place the public card would print.
 *
 * `show_address` is the same switch the backend honours: it only puts the
 * street into the public payload when the flag is on. With the flag off, or
 * with no address typed, the card falls back to the city — never to the
 * district, and never by re-deriving a city from the address text.
 */
export function previewLocation(form, cities) {
  if (form.show_address) {
    const address = text(form.address);
    if (address) return address;
  }
  if (!text(form.city_id)) return null;
  // `cityLabel` takes a listing-shaped object; the form carries the same
  // `city_id`, and its last two fallbacks (city_code, city_id) are absent
  // here on purpose — an unresolved id is "no city", not a location.
  const label = cityLabel({ city_id: form.city_id }, cities);
  return label === "—" || label === form.city_id ? null : label;
}

/**
 * The image the card should show.
 *
 * Primary first, then the lowest position, then whatever came first — the
 * same precedence the server sorts by, applied here because the editor
 * appends freshly uploaded images to the end of its own list.
 *
 * Images the user picked but has not uploaded yet are not considered: they
 * exist only as File objects, and turning them into object URLs would be a
 * second image pipeline this preview does not need.
 */
export function previewImageUrl(images) {
  const usable = (Array.isArray(images) ? images : []).filter(
    (img) => img && typeof img.url === "string" && img.url.trim() !== ""
  );
  if (usable.length === 0) return null;

  const main = usable.find((img) => img.is_main === true);
  if (main) return main.url.trim();

  const positioned = usable
    .filter((img) => Number.isFinite(Number(img.position)))
    .sort((a, b) => Number(a.position) - Number(b.position));
  return (positioned[0] || usable[0]).url.trim();
}

/**
 * Builds the whole preview model. Every field is either presentable or null.
 */
export function buildListingPreviewModel({ form, cities, images } = {}) {
  const f = form || {};
  const price = count(f.price_chf_month);

  return {
    title: text(f.title_de),
    description: text(markdownToPlainText(f.description_de)),
    priceLabel: price === null ? null : formatChf(price),
    location: previewLocation(f, cities),
    imageUrl: previewImageUrl(images),
    bedrooms: count(f.bedrooms),
    bathrooms: count(f.bathrooms),
    sizeSqm: count(f.size_sqm),
  };
}
